import { useCallback, useEffect, useState } from "react";
import { useAppStore } from "src/stores/appStore";
import { createSafeStorage } from "src/stores/safeStorage";

const MAX_SAVED_TAGS = 30;
const MAX_TAG_LENGTH = 60;
// The fixed menu choices. A custom tag that only differs from one of these by
// case is never a new shortcut: the menu hides it, so saving it would just
// burn one of the 30 slots for good.
export const BUILT_IN_TAGS = ["cliché", "repetitive", "doesn't sound like me", "confusing", "great line"];
const BUILT_IN_KEYS = new Set(BUILT_IN_TAGS.map((tag) => tag.toLowerCase()));
const storageKey = (userId) => `benchmark-custom-tags:${encodeURIComponent(userId)}`;

function storage() {
  try {
    return createSafeStorage(window.localStorage);
  } catch {
    return createSafeStorage(null);
  }
}

function normalizeTags(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value.filter((tag) => typeof tag === "string")
    .map((tag) => tag.trim().slice(0, MAX_TAG_LENGTH))
    .filter((tag) => {
      const key = tag.toLowerCase();
      if (!tag || seen.has(key) || BUILT_IN_KEYS.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, MAX_SAVED_TAGS);
}

function loadTags(userId) {
  if (!userId) return [];
  try {
    return normalizeTags(JSON.parse(storage().getItem(storageKey(userId))));
  } catch {
    return [];
  }
}

/** Personal shortcuts survive comparisons and reloads on this browser. */
export function useSavedBenchmarkTags() {
  const userId = useAppStore((state) => state.user?.uid || null);
  const [saved, setSaved] = useState(() => ({ userId, tags: loadTags(userId) }));

  useEffect(() => {
    const refresh = (event) => {
      if (!event || event.key === null || event.key === storageKey(userId)) {
        setSaved({ userId, tags: loadTags(userId) });
      }
    };
    refresh();
    window.addEventListener("storage", refresh);
    return () => window.removeEventListener("storage", refresh);
  }, [userId]);

  const rememberTag = useCallback((tag) => {
    // A callback from the previous account must not write into either account.
    if (!userId || useAppStore.getState().user?.uid !== userId) return;
    setSaved((previous) => {
      const previousTags = previous.userId === userId ? previous.tags : [];
      const tags = normalizeTags([tag, ...loadTags(userId), ...previousTags]);
      storage().setItem(storageKey(userId), JSON.stringify(tags));
      return { userId, tags };
    });
  }, [userId]);

  // Clear the old account's labels during the render before its effect runs.
  return { savedTags: saved.userId === userId ? saved.tags : [], rememberTag };
}
